<div class="day-summary-bar">
  @if (recalculating) {
    <div class="recalc-banner">
      <i class="pi pi-sync"></i>
      <span>Recalcul de la tournée en cours…</span>
    </div>
  }

  <!-- Proposition issue de la passe globale : elle déplace des interventions
       programmées sur un AUTRE jour — parfois hors de la tournée affichée,
       donc invisibles dans la timeline sans ce bloc. -->
  @if (requiresDayShift && dayShiftMoves.length > 0) {
    <div class="day-shift-banner">
      <button
        type="button"
        class="day-shift-banner-title"
        (click)="toggleDayShiftExpanded()"
        [pTooltip]="
          dayShiftExpanded ? 'Replier le détail' : 'Afficher le détail'
        "
        tooltipPosition="top"
      >
        <i class="pi pi-calendar-plus"></i>
        <span>
          {{ dayShiftMoves.length }} changement{{
            dayShiftMoves.length > 1 ? 's' : ''
          }}
          de jour
        </span>
        <i
          class="pi day-shift-chevron"
          [class.pi-chevron-down]="!dayShiftExpanded"
          [class.pi-chevron-up]="dayShiftExpanded"
        ></i>
      </button>
      @if (dayShiftExpanded) {
        @for (move of dayShiftMoves; track $index) {
          <div class="day-shift-move">
            <span class="day-shift-move-name">{{ move.name }}</span>
            @if (move.originalDate && move.newDate) {
              <span class="day-shift-move-dates">
                {{ formatDate(move.originalDate) }}
                <i class="pi pi-arrow-right"></i>
                {{ formatDate(move.newDate) }}
                @if (move.newStart) {
                  à {{ formatTime(move.newStart) }}
                }
              </span>
            }
            @if (move.postponablePeriod) {
              <span
                class="day-shift-move-period"
                [pTooltip]="postponableLabel(move.postponablePeriod)"
                tooltipPosition="top"
              >
                {{ move.postponablePeriod === 'week' ? 'semaine' : 'mois' }}
              </span>
            }
          </div>
        }
      }
    </div>
  }

  <!-- En-tête : compteurs à programmer / programmées + masquage rapide -->
  <div class="tl-header">
    <span class="tl-chip tl-chip-pool">
      <span class="tl-chip-dot"></span>
      {{ poolCount }} à programmer
    </span>
    @if (movedCount > 0) {
      <span
        class="tl-chip tl-chip-moved"
        pTooltip="Interventions déjà programmées dont l'horaire doit être modifié"
        tooltipPosition="top"
      >
        <i class="pi pi-arrows-h"></i>
        {{ movedCount }} à déplacer
      </span>
    }
    @if (movedDayCount > 0) {
      <span
        class="tl-chip tl-chip-moved-day"
        pTooltip="Interventions déjà programmées à déplacer sur un autre jour (autorisé par EROS)"
        tooltipPosition="top"
      >
        <i class="pi pi-calendar-plus"></i>
        {{ movedDayCount }} chgt de jour
      </span>
    }
    @if (scheduledCount > 0) {
      <button
        type="button"
        class="tl-chip tl-chip-sched"
        [class.is-off]="hideScheduled"
        (click)="toggleHideScheduled()"
        [pTooltip]="
          hideScheduled
            ? 'Afficher les interventions déjà programmées'
            : 'Masquer les interventions déjà programmées'
        "
        tooltipPosition="top"
      >
        <i
          class="pi"
          [class.pi-eye]="!hideScheduled"
          [class.pi-eye-slash]="hideScheduled"
        ></i>
        {{ scheduledCount }} programmée{{ scheduledCount > 1 ? 's' : '' }}
      </button>
    }
  </div>

  <div class="day-timeline">
    <!-- Départ domicile ou nuit à l'hôtel la veille -->
    @if (startStep) {
      @if (startStep.locationType === 'hotel') {
        <div
          class="tl-row tl-home tl-hotel"
          pTooltip="Nuit à l'hôtel la veille : le trajet vers la 1ère intervention n'est pas compté"
          tooltipPosition="top"
        >
          <i class="pi pi-building"></i>
          <span class="tl-name">Hôtel</span>
          <span class="tl-loc">Nuit sur place</span>
        </div>
      } @else {
        <div class="tl-row tl-home">
          <i class="pi pi-home"></i>
          <span class="tl-time">{{ formatTime(startStep.departure) }}</span>
          <span class="tl-name">Domicile</span>
          @if (selectedTechnicianHomeAddress) {
            <span class="tl-loc">{{ selectedTechnicianHomeAddress }}</span>
          }
        </div>
      }
    }

    @for (step of selectedTourSteps; track $index) {
      <!-- ═══ Intervention déjà programmée : ligne compacte dépliable ═══ -->
      @if (step.type === 'scheduled_intervention') {
        <!-- Les interventions à déplacer restent visibles même quand les
             programmées sont masquées : une action est attendue. -->
        @if (!hideScheduled || step.moved) {
          @if (step.travelMin > 0) {
            <div class="travel-separator">
              <span class="travel-sep-text">
                <i class="pi pi-car"></i>
                {{ formatDuration(step.travelMin) }}
                @if (step.distanceKm != null && step.distanceKm > 0) {
                  · {{ formatDistance(step.distanceKm) }}
                }
              </span>
            </div>
          }
          <div class="tl-stop">
            <div class="tl-sched-row" [class.tl-sched-moved]="step.moved">
              <div class="tl-sched-line">
                @if (step.tourStepNumber) {
                  <span class="tl-num-sm">{{ step.tourStepNumber }}</span>
                }
                <span class="tl-diamond"></span>
                <span class="tl-time-sm">
                  {{ formatTime(step.plannedStart ?? step.arrival) }}
                  @if (step.plannedEnd || step.departure) {
                    - {{ formatTime(step.plannedEnd ?? step.departure) }}
                  }
                </span>
                <span class="tl-name-sm">{{ step.name }}</span>
              </div>
              <!-- 2e ligne : ville + plage horaire + décalage autorisé + lien fiche -->
              @if (
                step.postalCode ||
                step.locality ||
                timeSlotLabel(step) ||
                (step.postponablePeriod && !step.moved) ||
                (step.interventionId && step.erpSource)
              ) {
                <div class="tl-sched-line tl-sched-meta">
                  @if (step.postalCode || step.locality) {
                    <span class="tl-loc-sm">
                      <i class="pi pi-map-marker"></i>
                      {{ step.postalCode }} {{ step.locality }}
                    </span>
                  }
                  @if (timeSlotLabel(step); as slot) {
                    <span
                      class="tl-timeslot"
                      pTooltip="Plage horaire imposée par le client"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-clock"></i>
                      {{ slot }}
                    </span>
                  }
                  @if (step.postponablePeriod && !step.moved) {
                    <span
                      class="tl-postponable"
                      [pTooltip]="postponableLabel(step.postponablePeriod)"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-history"></i>
                      {{
                        step.postponablePeriod === 'day'
                          ? 'journée'
                          : step.postponablePeriod === 'week'
                            ? 'semaine'
                            : 'mois'
                      }}
                    </span>
                  }
                  @if (step.interventionId && step.erpSource) {
                    <a
                      class="tl-fiche"
                      [href]="
                        '/intervention/' +
                        step.interventionId +
                        '/view/' +
                        step.erpSource
                      "
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i class="pi pi-eye"></i>
                      Voir
                    </a>
                  }
                </div>
              }
              @if (step.moved) {
                <div class="tl-sched-line tl-sched-line-badge">
                  @if (step.movedDay) {
                    <span
                      class="tl-moved-inline tl-moved-day-inline"
                      pTooltip="EROS autorise le décalage de cette intervention : la proposition la déplace sur un autre jour de sa période"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-calendar-plus"></i>
                      Changer de jour
                      @if (step.originalDate && step.newDate) {
                        {{ formatDate(step.originalDate) }} →
                        {{ formatDate(step.newDate) }}
                        @if (step.newStart) {
                          à {{ formatTime(step.newStart) }}
                        }
                      }
                    </span>
                  } @else {
                    <span
                      class="tl-moved-inline"
                      pTooltip="Intervention déjà programmée : son horaire doit être modifié pour que la tournée fonctionne"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-arrows-h"></i>
                      À déplacer
                      @if (step.originalStart && step.newStart) {
                        {{ formatTime(step.originalStart) }} →
                        {{ formatTime(step.newStart) }}
                      }
                    </span>
                  }
                  @if (step.postponablePeriod) {
                    <span
                      class="tl-postponable"
                      [pTooltip]="postponableLabel(step.postponablePeriod)"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-history"></i>
                      {{
                        step.postponablePeriod === 'day'
                          ? 'journée'
                          : step.postponablePeriod === 'week'
                            ? 'semaine'
                            : 'mois'
                      }}
                    </span>
                  }
                </div>
              }
            </div>
          </div>
        }
      } @else if (step.type !== 'start' && step.type !== 'end') {
        <!-- ═══ Affaire du vivier : carte « À programmer » mise en avant ═══ -->
        @if (step.travelMin > 0) {
          <div class="travel-separator">
            <span class="travel-sep-text">
              <i class="pi pi-car"></i>
              {{ formatDuration(step.travelMin) }}
              @if (step.distanceKm != null && step.distanceKm > 0) {
                · {{ formatDistance(step.distanceKm) }}
              }
            </span>
          </div>
        }
        <div class="tl-stop">
          <div
            class="tl-row tl-pool"
            [attr.data-affair-id]="step.affairId"
            [class.tl-current]="step.isCurrentAffair"
            [class.tl-linkable]="!!step.affairId"
            (click)="onStepClick(step, $event)"
          >
            <!-- Ligne principale : n° · heure · nom · chip -->
            <div class="tl-main">
              @if (step.tourStepNumber) {
                <span class="tl-num">{{ step.tourStepNumber }}</span>
              }
              <span class="tl-time">
                @if (step.plannedStart) {
                  {{ formatTime(step.plannedStart) }}
                } @else {
                  {{ formatTime(step.arrival) }}
                }
                @if (step.plannedEnd || step.departure) {
                  - {{ formatTime(step.plannedEnd ?? step.departure) }}
                }
              </span>
              @if (
                step.arrival &&
                step.plannedStart &&
                formatTime(step.arrival) !== formatTime(step.plannedStart)
              ) {
                <span class="tl-arrival" title="Arrivée estimée sur place">
                  <i class="pi pi-map-marker"></i>
                  {{ formatTime(step.arrival) }}
                </span>
              }
              <span class="tl-name">{{ step.name }}</span>
              @if (isAffairOptimizing(step.affairId)) {
                <span
                  class="tl-badge tl-optimizing"
                  title="Ré-optimisation en cours"
                >
                  <i class="pi pi-spin pi-spinner"></i>
                </span>
              }
              <span class="tl-pool-chip">À programmer</span>
            </div>

            <!-- Ligne secondaire : l'essentiel seulement (localité, échéance, groupe) -->
            @if (
              step.groupName ||
              step.postalCode ||
              step.locality ||
              planningWindowLabel(step) ||
              step.planningDeadline
            ) {
              <div class="tl-meta">
                @if (step.postalCode || step.locality) {
                  <span class="tl-loc">
                    <i class="pi pi-map-marker"></i>
                    {{ step.postalCode }} {{ step.locality }}
                  </span>
                }
                <!-- Période souhaitée OU échéance : jamais les deux (mutuellement exclusives). -->
                @if (planningWindowLabel(step); as window) {
                  <span
                    class="tl-deadline tl-window"
                    pTooltip="Période de planification souhaitée (EROS)"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-calendar"></i>
                    {{ window }}
                  </span>
                } @else if (step.planningDeadline) {
                  <span
                    class="tl-deadline"
                    pTooltip="Échéance de planification"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-calendar-times"></i>
                    {{ formatDate(step.planningDeadline) }}
                  </span>
                }
                @if (step.groupName) {
                  <span class="tl-group">{{ step.groupName }}</span>
                }
              </div>
            }

            <!-- Actions et détails : carte active + première affaire à programmer -->
            @if (isCardExpanded(step)) {
              <div class="tl-actions">
                @if (step.postalCode || step.locality) {
                  <button
                    type="button"
                    class="tl-action-btn"
                    [class.copied]="copiedAddressAffairId === step.affairId"
                    (click)="onCopyAddress(step, $event)"
                  >
                    <i
                      class="pi"
                      [class.pi-copy]="copiedAddressAffairId !== step.affairId"
                      [class.pi-check]="copiedAddressAffairId === step.affairId"
                    ></i>
                    {{
                      copiedAddressAffairId === step.affairId
                        ? 'Copié'
                        : 'CP + ville'
                    }}
                  </button>
                }
                @if (step.tablotFicheUrl) {
                  <a
                    class="tl-action-btn"
                    [href]="step.tablotFicheUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    (click)="$event.stopPropagation()"
                  >
                    <i class="pi pi-external-link"></i>
                    Fiche
                  </a>
                }
                @if (step.erosScheduleUrl) {
                  <a
                    class="tl-action-btn"
                    [href]="step.erosScheduleUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    (click)="$event.stopPropagation()"
                    title="Valider une proposition et créer l'intervention sur EROS"
                  >
                    <i class="pi pi-external-link"></i>
                    Programmer
                  </a>
                }
                @if (step.poolScheduleDialog) {
                  <button
                    type="button"
                    class="tl-action-btn"
                    (click)="
                      $event.stopPropagation();
                      manualScheduleRequested.emit(step)
                    "
                    title="Valider un créneau et créer l'intervention sur EROS"
                  >
                    <i class="pi pi-bolt"></i>
                    Programmer
                  </button>
                }
                @if (step.manualDraft) {
                  <button
                    type="button"
                    class="tl-action-btn tl-action-danger"
                    (click)="
                      $event.stopPropagation(); manualDeleteRequested.emit(step)
                    "
                    title="Supprimer cette affaire manuelle du vivier"
                  >
                    <i class="pi pi-trash"></i>
                    Supprimer
                  </button>
                }
              </div>
            }
          </div>

          <!-- Motif / commentaire du dernier appel : carte dépliée uniquement -->
          @if (isCardExpanded(step)) {
            @if (step.lastCallReasonType) {
              <div class="tl-extra tl-call-reason">
                <i class="pi pi-phone"></i>
                <span class="call-reason-type">{{
                  step.lastCallReasonType
                }}</span>
              </div>
            }
            @if (step.lastCallReasonComment) {
              <div class="tl-extra tl-call-comment">
                <i class="pi pi-comment"></i>
                <span>{{ step.lastCallReasonComment }}</span>
              </div>
            }
          }

          <!-- Autres créneaux disponibles pour cette affaire (au clic) -->
          @if (
            isCardExpanded(step) &&
            step.alternativeSlots &&
            step.alternativeSlots.length > 0
          ) {
            <div class="alt-slots">
              <div class="alt-slots-label">
                <i class="pi pi-calendar-plus"></i>
                Autres dates disponibles
              </div>
              @for (
                slot of step.alternativeSlots;
                track slot.date + ':' + slot.technicianId
              ) {
                <button
                  type="button"
                  class="alt-slot-item"
                  (click)="
                    alternativeSlotNavigated.emit({
                      slot: slot,
                      affairId: step.affairId!,
                    })
                  "
                >
                  <span class="alt-slot-date">{{ formatDate(slot.date) }}</span>
                  <span class="alt-slot-tech">
                    <i class="pi pi-user"></i>
                    {{ slot.technicianName }}
                  </span>
                  <div class="alt-slot-badges">
                    @if (slot.travelDurationMin) {
                      <span class="badge-duration">
                        <i class="pi pi-car"></i>
                        {{ formatDuration(slot.travelDurationMin) }}
                      </span>
                    }
                    @if (slot.tourTotalDurationMin) {
                      <span class="badge-slots">
                        {{ formatDuration(slot.tourTotalDurationMin) }}
                        tournée
                      </span>
                    }
                  </div>
                </button>
              }
            </div>
          }
        </div>
      }
    }

    <!-- Retour domicile ou nuit à l'hôtel ce soir -->
    @if (endStep) {
      @if (endStep.locationType === 'hotel') {
        <div
          class="tl-row tl-home tl-hotel"
          pTooltip="Nuit à l'hôtel ce soir : le trajet de retour au domicile n'est pas compté"
          tooltipPosition="top"
        >
          <i class="pi pi-building"></i>
          <span class="tl-name">Hôtel</span>
          <span class="tl-loc">Nuit sur place</span>
        </div>
      } @else {
        @if (endStep.travelMin > 0) {
          <div class="travel-separator">
            <span class="travel-sep-text">
              <i class="pi pi-car"></i>
              {{ formatDuration(endStep.travelMin) }}
              @if (endStep.distanceKm != null && endStep.distanceKm > 0) {
                · {{ formatDistance(endStep.distanceKm) }}
              }
            </span>
          </div>
        }
        <div class="tl-row tl-home">
          <i class="pi pi-home"></i>
          <span class="tl-time">{{ formatTime(endStep.arrival) }}</span>
          <span class="tl-name">Retour domicile</span>
        </div>
      }
    }
  </div>
</div>
